import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExecutions } from '../../api/executions.js';
import { useDownloadPayments, usePayments } from '../../api/payments.js';
import { formatAmount, formatDateTime } from '../../utils/format.js';
import { originLabel, paymentTypeLabel, serviceTypeLabel, stepTypeLabel } from '../../utils/constants.js';
import Spinner from '../Spinner.jsx';
import ErrorBanner from '../ErrorBanner.jsx';
import StatusBadge from '../StatusBadge.jsx';

const PAGE_SIZE = 10;

export default function PaymentsPanel({ companyId, processId, executionId }) {
    const { t, i18n } = useTranslation();
    const [page, setPage] = useState(0);

    const { data: executions } = useExecutions(companyId, processId);
    const { data: paymentsPage, isLoading, error } = usePayments(companyId, executionId, page, PAGE_SIZE);
    const download = useDownloadPayments(companyId, executionId);

    const execution = executions?.find((e) => e.id === executionId);
    const payments = paymentsPage?.content ?? [];
    const pageInfo = paymentsPage?.page;

    return (
        <div className="workspace-panel payments-panel">
            <div className="panel-header">
                <div className="panel-header-titles">
                    <h2>{t('payments.title')}</h2>
                    {execution && (
                        <div className="panel-subtitle">
                            {t('payments.started', { date: formatDateTime(execution.startDate, i18n.language) })}
                        </div>
                    )}
                </div>
                {executionId && (
                    <button
                        type="button"
                        className="btn btn-sm"
                        disabled={download.isPending || payments.length === 0}
                        onClick={() => download.mutate()}
                    >
                        {download.isPending ? t('payments.downloading') : t('payments.download')}
                    </button>
                )}
            </div>

            {!executionId && <div className="panel-empty">{t('payments.selectExecutionPrompt')}</div>}

            {executionId && (
                <div className="panel-body">
                    {execution && (
                        <div className="steps-list" style={{ marginBottom: '1rem' }}>
                            {execution.steps.map((step) => (
                                <div className="step-row" key={step.index}>
                                    <span className="step-index">{step.index}</span>
                                    <span className="step-name">{stepTypeLabel(step.type, t)}</span>
                                    {step.attempt > 1 && (
                                        <span className="step-meta">{t('payments.attempt', { count: step.attempt })}</span>
                                    )}
                                    <StatusBadge status={step.status} />
                                </div>
                            ))}
                            {execution.resultDescription && (
                                <div className="error-banner">{execution.resultDescription}</div>
                            )}
                        </div>
                    )}

                    <ErrorBanner error={download.error} fallback={t('payments.downloadError')} />
                    <ErrorBanner error={error} fallback={t('payments.loadError')} />

                    {isLoading && <Spinner label={t('payments.loading')} />}

                    {!isLoading && payments.length === 0 && (
                        <div className="panel-empty">{t('payments.empty')}</div>
                    )}

                    {!isLoading && payments.length > 0 && (
                        <>
                            <div className="table-scroll">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t('payments.columns.type')}</th>
                                            <th>{t('payments.columns.series')}</th>
                                            <th>{t('payments.columns.number')}</th>
                                            <th>{t('payments.columns.emissionDate')}</th>
                                            <th>{t('payments.columns.currency')}</th>
                                            <th>{t('payments.columns.origin')}</th>
                                            <th>{t('payments.columns.service')}</th>
                                            <th>{t('payments.columns.issuerName')}</th>
                                            <th>{t('payments.columns.issuerRuc')}</th>
                                            <th className="numeric">{t('payments.columns.taxable')}</th>
                                            <th className="numeric">{t('payments.columns.igv')}</th>
                                            <th className="numeric">{t('payments.columns.nonTaxable')}</th>
                                            <th className="numeric">{t('payments.columns.otherTaxes')}</th>
                                            <th className="numeric">{t('payments.columns.total')}</th>
                                            <th>{t('payments.columns.description')}</th>
                                            <th>{t('payments.columns.extractionNotes')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((payment) => (
                                            <tr key={payment.id}>
                                                <td>{paymentTypeLabel(payment.info?.type)}</td>
                                                <td>{payment.info?.series ?? '—'}</td>
                                                <td>{payment.info?.number ?? '—'}</td>
                                                <td>{payment.info?.emissionDate ?? '—'}</td>
                                                <td>{payment.info?.currency ?? '—'}</td>
                                                <td>{originLabel(payment.info?.origin)}</td>
                                                <td>{serviceTypeLabel(payment.serviceType, t)}</td>
                                                <td>{payment.issuer?.name ?? '—'}</td>
                                                <td>{payment.issuer?.ruc ?? '—'}</td>
                                                <td className="numeric">
                                                    {formatAmount(payment.values?.taxableAmount, i18n.language)}
                                                </td>
                                                <td className="numeric">{formatAmount(payment.values?.igv, i18n.language)}</td>
                                                <td className="numeric">
                                                    {formatAmount(payment.values?.nonTaxableAmount, i18n.language)}
                                                </td>
                                                <td className="numeric">
                                                    {formatAmount(payment.values?.otherTaxesAmount, i18n.language)}
                                                </td>
                                                <td className="numeric">
                                                    {formatAmount(payment.values?.totalAmount, i18n.language)}
                                                </td>
                                                <td className="wrap">{payment.description ?? '—'}</td>
                                                <td className="wrap">{payment.extractionNotes ?? '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="pagination">
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    disabled={page === 0}
                                    onClick={() => setPage((p) => p - 1)}
                                >
                                    {t('common.previous')}
                                </button>
                                <span>
                                    {t('common.pageOf', { page: page + 1, total: Math.max(pageInfo?.totalPages ?? 1, 1) })}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    disabled={pageInfo ? page + 1 >= pageInfo.totalPages : true}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    {t('common.next')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
